import { Trans, useLingui } from "@lingui/react/macro";
import type { WorkOverview } from "@rakazo/contracts";
import { isRetryableError } from "@rakazo/core";
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogTrigger,
  Spinner,
} from "@rakazo/ui-web";
import { useQuery } from "@tanstack/react-query";
import { ListTodo, X } from "lucide-react";
import { useState } from "react";
import { IconTooltip } from "../components/IconTooltip";
import { activityPreview } from "../lib/activity-list-filters";
import { useSpaceQueries } from "../lib/query";
import { statusLabel, statusTone } from "./ActivityList";

export type WorkTarget = { botId: string; groupId?: string | null; routineId?: string };

/** The same live work list is shared by the web UI and its desktop host. */
export function WorkModal({ onOpenTask }: { onOpenTask: (target: WorkTarget) => void }) {
  const { t, i18n } = useLingui();
  const [open, setOpen] = useState(false);
  const queries = useSpaceQueries();
  const work = useQuery({
    ...queries.runs.work.queryOptions(),
    enabled: open,
    refetchInterval: (query) =>
      !open || (query.state.error && !isRetryableError(query.state.error)) ? false : 10_000,
  });
  const openTask = (target: WorkTarget) => {
    setOpen(false);
    onOpenTask(target);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <IconTooltip label={t`Work`}>
        <DialogTrigger
          render={<Button variant="ghost" size="icon-sm" />}
          aria-label={t`Work`}
          className="app-no-drag flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground/70 hover:text-foreground/75"
        >
          <ListTodo size={15} strokeWidth={1.8} aria-hidden="true" />
        </DialogTrigger>
      </IconTooltip>
      <DialogContent
        showCloseButton={false}
        data-testid="work-modal"
        className="app-no-drag flex max-h-[min(80dvh,640px)] flex-col gap-0 overflow-hidden p-0 sm:max-w-md"
      >
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <DialogTitle className="text-base">
            <Trans>Work</Trans>
          </DialogTitle>
          <DialogClose render={<Button variant="ghost" size="icon-sm" />} aria-label={t`Close`}>
            <X size={16} aria-hidden="true" />
          </DialogClose>
        </div>
        <div className="rk-scroll min-h-0 overflow-y-auto p-3" aria-busy={work.isFetching}>
          {work.isError ? (
            <div
              role="alert"
              className="mb-2 flex items-center justify-between gap-3 px-2 text-sm text-destructive"
            >
              <span>
                {work.data ? (
                  <Trans>Could not refresh work.</Trans>
                ) : (
                  <Trans>Could not load work.</Trans>
                )}
              </span>
              <Button
                variant="ghost"
                size="sm"
                disabled={work.isFetching}
                onClick={() => void work.refetch()}
              >
                <Trans>Try again</Trans>
              </Button>
            </div>
          ) : null}
          {work.isPending ? (
            <div className="flex justify-center p-6">
              <Spinner aria-label={t`Loading work…`} />
            </div>
          ) : work.data ? (
            <WorkList data={work.data} locale={i18n.locale || "en"} onOpenTask={openTask} />
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function WorkList({
  data,
  locale,
  onOpenTask,
}: {
  data: WorkOverview;
  locale: string;
  onOpenTask: (target: WorkTarget) => void;
}) {
  const { t } = useLingui();
  const formatDate = (iso: string) =>
    new Intl.DateTimeFormat(locale, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
    }).format(new Date(iso));
  return (
    <>
      <section aria-label={t`Active now`}>
        <h2 className="px-2 py-2 text-xs font-medium text-muted-foreground">
          <Trans>Active now</Trans> <span className="tabular-nums">{data.runs.length}</span>
        </h2>
        {data.runs.length ? (
          <ul className="space-y-1">
            {data.runs.map((run) => (
              <li key={run.runId}>
                <button
                  type="button"
                  onClick={() => onOpenTask(run)}
                  className="w-full rounded-lg px-2 py-2 text-start hover:bg-accent focus-visible:outline-ring"
                >
                  <span className="flex items-baseline justify-between gap-3 text-sm">
                    <span className="min-w-0 truncate font-medium">
                      {run.botName}
                      {run.groupName ? ` · ${run.groupName}` : ""}
                    </span>
                    <span className={`shrink-0 text-xs ${statusTone(run.status, run.updatedAt)}`}>
                      {statusLabel(run.status)}
                    </span>
                  </span>
                  <span className="mt-1 block line-clamp-2 break-words text-sm text-muted-foreground">
                    {activityPreview(run)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="px-2 py-3 text-sm text-muted-foreground">
            <Trans>No active tasks.</Trans>
          </p>
        )}
      </section>
      <section aria-label={t`Up next`} className="mt-3 border-t border-border pt-2">
        <h2 className="px-2 py-2 text-xs font-medium text-muted-foreground">
          <Trans>Up next</Trans> <span className="tabular-nums">{data.scheduled.length}</span>
        </h2>
        {data.scheduled.length ? (
          <ul className="space-y-1">
            {data.scheduled.map((task) => (
              <li key={task.routineId}>
                <button
                  type="button"
                  onClick={() => onOpenTask(task)}
                  className="w-full rounded-lg px-2 py-2 text-start hover:bg-accent focus-visible:outline-ring"
                >
                  <span className="block text-sm font-medium">{task.name}</span>
                  <span className="mt-1 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 text-xs text-muted-foreground">
                    <span>{task.botName}</span>
                    <time dateTime={task.nextRunAt} title={formatDate(task.nextRunAt)}>
                      {Date.parse(task.nextRunAt) <= Date.now()
                        ? t`Due now`
                        : formatDate(task.nextRunAt)}
                    </time>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="px-2 py-3 text-sm text-muted-foreground">
            <Trans>No scheduled tasks.</Trans>
          </p>
        )}
      </section>
    </>
  );
}
